import { useState, useImperativeHandle, forwardRef, useRef } from "react";
import {
  Select,
  MenuItem,
  FormControl,
  Checkbox,
  ListItemText,
} from "@mui/material";
import type { ICellEditorParams } from "ag-grid-community";
import { ChevronDown } from "../icons/ChevronDown";
import "../../assets/css/gridSelectCell.css";

interface Option {
  id: number | string;
  label: string;
}

interface Props extends ICellEditorParams {
  values: Option[];
  originalValues?: any[];
  multiple?: boolean;
  label: string;
  labelKey?: string;
  /** Set by createStringSelectColumn — square corners (no border radius) */
  stringSelectColumn?: boolean;
  /** Set by createSingleSelectColumn — square input + dropdown paper (no border radius) */
  squareSelectCorners?: boolean;
}
// const { updateDeal } = useDealsProvider();

export const MUISelectCellEditor = forwardRef<any, Props>(
  (
    {
      value,
      values,
      originalValues,
      multiple,
      label,
      stringSelectColumn,
      squareSelectCorners,
      api,
      colDef,
      node,
    },
    ref,
  ) => {
    const sourceValues = originalValues || values;

    const initialSelected = multiple
      ? sourceValues.filter(
          (option) =>
            Array.isArray(value) && value.some((v) => v?.id === option.id),
        )
      : (() => {
          // Handle null value - always return null option object if value is null/undefined
          if (value === null || value === undefined) {
            const nullOption = sourceValues.find(
              (option) => option.id === null,
            );
            return nullOption || { id: null, label: "(None)" };
          }
          // If value is an object with id null, return null option
          if (value && typeof value === "object" && value.id === null) {
            const nullOption = sourceValues.find(
              (option) => option.id === null,
            );
            return nullOption || { id: null, label: "(None)" };
          }
          const match = sourceValues.find(
            (option) => option.id === (value?.id ?? value),
          );
          return (
            match ||
            sourceValues.find((option) => option.id === null) || {
              id: null,
              label: "(None)",
            }
          );
        })();

    const [selected, setSelected] = useState<any>(
      initialSelected ?? { id: null, label: "(None)" },
    );
    // A pick ends the edit in handleChange; closing without one ends it in onClose.
    const pickedRef = useRef(false);

    useImperativeHandle(ref, () => ({
      getValue: () => {
        return selected;
      },
      isCancelAfterEnd: () => false,
    }));

    const handleChange = (e: any) => {
      const newValue = e.target.value;
      const filteredValue = multiple
        ? sourceValues.filter((option) => newValue.includes(option.id)) // Return full original objects
        : (() => {
            // Handle null/empty selection - empty string means null option was selected
            if (
              newValue === null ||
              newValue === "" ||
              newValue === undefined
            ) {
              const nullOption = sourceValues.find(
                (option) => option.id === null,
              );
              return nullOption || { id: null, label: "(None)" };
            }
            const match = sourceValues.find((option) => option.id === newValue);
            return (
              match ||
              sourceValues.find((option) => option.id === null) || {
                id: null,
                label: "(None)",
              }
            );
          })();

      pickedRef.current = true;
      setSelected(filteredValue);

      // Update the node data immediately to trigger onCellValueChanged
      const colKey = colDef.colId ?? colDef.field!;
      node.setDataValue(colKey, filteredValue);

      // Stop editing after a short delay to allow the value to be processed
      setTimeout(() => {
        api.stopEditing(true);
      }, 50);
    };

    // Ensure value is never null/undefined for MUI Select - use empty string for null selection
    const selectValue = multiple
      ? selected && Array.isArray(selected)
        ? selected.map((s: Option) => s.id)
        : []
      : selected && selected.id !== null && selected.id !== undefined
        ? selected.id
        : "";

    const squareCorners = stringSelectColumn || squareSelectCorners;

    const selectClassName = [
      "custom-select",
      squareCorners ? "custom-select--string-column" : "",
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <FormControl
        fullWidth
        size="small"
        margin="none"
        sx={{ my: 0, minHeight: 0, borderWidth: "0px !important" }}
      >
        <Select
          className={selectClassName}
          size="small"
          displayEmpty={!multiple}
          multiple={multiple}
          value={selectValue}
          defaultOpen={true}
          onChange={handleChange}
          onClose={(event) => {
            if (pickedRef.current) return;
            // Closed by clicking elsewhere (e.g. another row): end the edit,
            // keeping the value, and pass the click on to what was under the
            // menu's backdrop so that row is selected in the same click.
            const { clientX, clientY } = event as unknown as MouseEvent;
            setTimeout(() => {
              api.stopEditing(true);
              if (typeof clientX !== "number" || typeof clientY !== "number") {
                return;
              }
              const target = document.elementFromPoint?.(clientX, clientY);
              if (
                target instanceof HTMLElement &&
                !target.closest(".ag-custom-component-popup")
              ) {
                target.click();
              }
            }, 0);
          }}
          inputProps={{ "aria-label": label }}
          IconComponent={ChevronDown}
          renderValue={(selectedIds) => {
            if (multiple) {
              return (selectedIds as any[])
                .map((id) => values.find((o) => o.id === id)?.label)
                .join(", ");
            }
            // Handle empty string case (null selection) - check both selectedIds and selected state
            if (
              selectedIds === "" ||
              selectedIds === null ||
              (selected === null && selectedIds === "")
            ) {
              const nullOption = values.find((o) => o.id === null);
              return nullOption?.label || "";
            }
            return values.find((o) => o.id === selectedIds)?.label || "";
          }}
          MenuProps={{
            // The menu is portalled outside the grid; this class tells AG Grid
            // it belongs to the editor, so stopEditingWhenCellsLoseFocus does
            // not close the editor as soon as the menu takes focus.
            className: "ag-custom-component-popup",
            PaperProps: {
              className: squareCorners
                ? "custom-select-menu-paper-string"
                : undefined,
              style: {
                maxHeight: 300,
                borderWidth: "0px !important",
                ...(squareCorners ? { borderRadius: 0 } : {}),
              },
            },
            disableAutoFocusItem: true,
            MenuListProps: {
              className: multiple
                ? "custom-multi-select-menu-list"
                : "custom-select-menu-list",
              onMouseDown: (e) => {
                // prevent the menu from closing on click
                e.stopPropagation();
              },
            },
          }}
        >
          {values.map((option) => {
            const isChecked = multiple
              ? selected?.some((s: Option) => s.id === option.id)
              : option.id === null
                ? selected == null || selected.id === null
                : selected?.id === option.id;
            // Use empty string for null option value to match selectValue when null is selected
            const menuItemValue = option.id === null ? "" : option.id;
            return (
              <MenuItem key={option.id ?? "null"} value={menuItemValue}>
                {multiple && <Checkbox checked={isChecked} />}
                <ListItemText primary={option.label} />
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    );
  },
);
